<template>
  <!-- on desktop -->
  <b-container v-if="$device.isDesktop" class="text-center text-white">
    <b-row align-h="center" align-v="center">
      <b-col class="col-lg-6">
        <img
          src="/images/DropsLogoSmal-ccl.gif"
          class="w-10"
          alt="Drops"
          style="max-height: 100px;"
        >
        <h2>
          DROPS
        </h2>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="col-lg-4" />
      <b-col class="col-lg-4">
        <h4>
          To view this video you'll need to be on a mobile device.
        </h4>
        <p>Please click or scan this QR code on your phone or tablet</p>
        <a :href="dropUrl" target="_blank">
          <vue-qrcode
            v-if="dropUrl"
            :value="dropUrl"
            dark="#212529"
            class="qr-code"
          />
        </a>
        <a :href="dropUrl" target="_blank" style="color: white;">
          {{ dropUrl }}
        </a>
      </b-col>
      <b-col class="col-lg-4" />
    </b-row>
  </b-container>
  <!-- Location consent screen -->
  <div v-else-if="isGeofenced && modalShow" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 px-4">
    <div class="bg-gray-800 border border-gray-700 rounded-xl max-w-sm w-full p-8 text-center space-y-6 shadow-2xl text-white">
      <div class="flex justify-center">
        <img src="/images/location-pin.png" alt="Location" class="h-10 w-10 object-contain">
      </div>
      <div class="space-y-2">
        <h2 class="text-xl font-semibold text-gray-100">
          Location Required
        </h2>
        <p class="text-gray-400 text-sm leading-relaxed">
          This drop is location-exclusive. Tap below to check if you're in range.
        </p>
      </div>
      <div class="flex flex-col gap-3">
        <button
          class="w-full py-3 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold transition-colors"
          @click="allowLocation"
        >
          Check my location
        </button>
        <button
          class="w-full py-2 px-4 text-gray-400 hover:text-gray-200 text-sm transition-colors"
          @click="declineLocation"
        >
          No thanks
        </button>
      </div>
    </div>
  </div>
  <!-- Loading/checking location -->
  <div v-else-if="loading" class="fixed inset-0 flex items-center justify-center bg-gray-900">
    <div class="text-center space-y-4">
      <div class="w-12 h-12 border-4 border-gray-600 border-t-blue-500 rounded-full animate-spin mx-auto" />
      <p v-if="isGeofenced" class="text-gray-400 text-sm">
        Checking your location...
      </p>
    </div>
  </div>
  <!-- Inside fence and on mobile/tablet -->
  <div v-else-if="insideFence" class="bg-black">
    <!--THIS IS WHERE THE MAGIC HAPPENS-->
    <VideoOnly v-if="drop" />
    <p v-else>
      Error: please refresh
    </p>
  </div>
  <!-- Outside fence / location not allowed -->
  <div
    v-else-if="isGeofenced && !insideFence"
    class="fixed inset-0 flex items-center justify-center px-4 bg-gray-900"
  >
    <div class="max-w-sm w-full text-center space-y-4">
      <!-- Outside fence with location allowed → show map -->
      <div v-if="locationAllowed">
        <GeoMap
          :countdown="countdownTime"
          :current-loc="[currentCoords.long, currentCoords.lat]"
          :drop-loc="[drop.point.coords[0], drop.point.coords[1]]"
          :description="drop.point.description ? drop.point.description : 'Drop is here'"
          :handle="drop.user.username ? drop.user.username : ''"
          :place-id="drop.point._creator"
        />
      </div>
      <!-- Location permission was blocked after clicking OK -->
      <div v-else-if="yesClicked" class="space-y-4">
        <div class="text-5xl">
          ⚠️
        </div>
        <p class="font-semibold text-gray-100">
          Location access didn't work.
        </p>
        <p class="text-sm text-gray-400">
          Check <em>Settings → Privacy → Location Services</em> on your device.
        </p>
        <div v-if="drop.altDropId" class="pt-2">
          <button class="tw-btn w-full justify-center" @click="gotoAlt()">
            View without location
          </button>
        </div>
      </div>
      <!-- Declined or no permission -->
      <div v-else class="space-y-4">
        <div class="text-5xl">
          🔒
        </div>
        <p class="font-semibold text-gray-100">
          No problem!
        </p>
        <p class="text-sm text-gray-400">
          Refresh the page if you change your mind.
        </p>
        <div v-if="drop.altDropId" class="pt-2">
          <button class="tw-btn w-full justify-center" @click="gotoAlt()">
            View without location
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import * as THREE from 'three'
import { mapState } from 'vuex'
import VueQrcode from 'vue-qrcode'
import VideoOnly from '~/components/scenes/VideoOnly.vue'
// import MainScene from '~/components/scenes/Main.vue'
import GeoMap from '~/components/common/GeoMap.vue'

export default {
  components: {
    // MainScene,
    VideoOnly,
    GeoMap,
    VueQrcode
  },
  props: {
    baseUrl: {
      type: String,
      default () {
        return 'https://drops.nyc/'
      }
    }
  },
  computed: {
    ...mapState(['drop'])
  },
  watch: {
    hasEnteredGeofence: {
      handler (value) {
        this.hasEnteredGeofence = value
      }
    }
  },
  data () {
    return {
      bypassFence: false,
      dropId: this.$route.params.media,
      locationId: this.$route.params.locationId,
      dropUrl: '',
      modalShow: false,
      loading: true,
      isGeofenced: false,
      insideFence: false,
      hasEnteredGeofence: false,
      locationAllowed: false,
      yesClicked: false,
      declinedGeo: false,
      geofence: null,
      currentCoords: null
    }
  },
  async mounted () {
    const urlParams = new URLSearchParams(window.location.search)
    const bypassFenceParam = urlParams.get('bypass')
    if (bypassFenceParam !== null) {
      this.bypassFence = bypassFenceParam
    }
    this.dropUrl = `${this.baseUrl}/m/${this.dropId}`

    try {
      const response = await this.$axios.get(`/pub/drop/${this.dropId}`)

      this.$store.commit('drop', response.data)

      if (this.drop.launchDate) {
        this.startTimer()
      }

      if (!this.drop.isGeofenced || this.bypassFence) {
        this.modalShow = false
        this.loading = false
        this.insideFence = true
        this.isGeofenced = false
        this.$store.dispatch('recordAndReward')
      } else {
        // Skip consent on return visits
        if (localStorage.getItem('geo_consented_' + this.dropId)) {
          this.modalShow = false
          this.yesClicked = true
          this.locationAllowed = true
          this.initGeofence()
        } else {
          // Skip consent if browser already granted permission
          navigator.permissions && navigator.permissions.query({ name: 'geolocation' }).then((PermissionStatus) => {
            if (PermissionStatus.state === 'granted') {
              this.modalShow = false
              this.yesClicked = true
              this.locationAllowed = true
              this.initGeofence()
            } else {
              this.modalShow = true
              this.yesClicked = false
              this.locationAllowed = false
            }
          })
        }
      }
    } catch (err) {
      // console.log(err)
    }

    if (this.drop.isGeofenced) {
      this.isGeofenced = true
    }
  },
  methods: {
    startTimer () {
      if (this.drop.launchDate) {
        const launchDate = new Date(this.drop.launchDate)
        const now = new Date()
        const diff = launchDate - now
        if (diff > 0) {
          this.countdownTime = Math.floor(diff / 1000)
          this.timer = setInterval(() => {
            this.countdownTime--
          }, 1000)
        } else {
          this.countdownTime = 0
          if (this.timer) { cancelinterval(this.timer) }
        }
      }
    },
    allowLocation () {
      localStorage.setItem('geo_consented_' + this.dropId, '1')
      this.modalShow = false
      this.yesClicked = true
      this.locationAllowed = true
      this.initGeofence()
    },
    declineLocation () {
      this.modalShow = false
      this.declinedGeo = true
    },
    initGeofence () {
      if ('geolocation' in navigator && this.locationAllowed) {
        this.geofence = {
          long: this.drop.point.coords[0],
          lat: this.drop.point.coords[1]
        }

        navigator.geolocation.watchPosition(
          (position) => {
            this.loading = true
            this.currentCoords = {
              long: position.coords.longitude,
              lat: position.coords.latitude
            }
            if (
              this.isInsideFence(
                this.geofence.lat,
                this.geofence.long,
                this.currentCoords.lat,
                this.currentCoords.long
              )
            ) {
              // console.log(this.currentCoords.long, this.currentCoords.lat)
              // console.log('WITHIN ' + this.drop.point.radius + ' METERS')
              this.insideFence = true
              this.loading = false
              // record
              // console.log('this.hasEnteredGeofence' + this.hasEnteredGeofence)
              if (!this.hasEnteredGeofence) {
                this.hasEnteredGeofence = true

                this.$store.commit('enterGeofence', this.hasEnteredGeofence)

                // TODO move to a record.enterGeofence handler...
                this.$store.dispatch('recordAndReward')
              }
            } else if (this.drop.altDropId) {
              window.location.href = `https://${window.location.hostname}/d/${this.drop.altDropId}`
            } else {
              // console.log(this.currentCoords.long, this.currentCoords.lat)
              // console.log('SORRY NOT CLOSE ENOUGH')
              this.insideFence = false
              this.loading = false
            }
          },
          (err) => {
            console.log(err.message)
            this.loading = false
            this.locationAllowed = false
          },
          { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
        )
      } else {
        // console.log('Geolocation not available')
      }
    },
    isInsideFence (lt, ln, lt1, ln1) {
      const R = 6371000 // Earth's radius in m
      const dLat = ((lt - lt1) * Math.PI) / 180
      const dLon = ((ln - ln1) * Math.PI) / 180
      const a =
        0.5 -
        Math.cos(dLat) / 2 +
        (Math.cos((lt1 * Math.PI) / 180) *
          Math.cos((lt * Math.PI) / 180) *
          (1 - Math.cos(dLon))) /
          2
      const d = Math.round(R * 2 * Math.asin(Math.sqrt(a)))

      return d < this.drop.point.radius
    },
    gotoAlt () {
      window.location.href = `https://${window.location.hostname}/d/${this.drop.altDropId}`
    }
  },

  head () {
    return {
      title: 'Drops: ' + this.drop.title !== '' ? this.drop.title : this.dropId
    }
  }
}
</script>

<style scoped>
.qr-code {
  max-width: 200px;
  max-height: 200px;
  margin: 0 auto;
}
.bg-gradient{
  background: linear-gradient(-45deg, #869DE4,#4A2695, #4A2695, #869DE4);
  color: white;
}
</style>
